import React, { useState, useEffect } from 'react';
import { Plus, Building2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Customer } from '../types';
import { CustomerCard } from '../components/CustomerCard';
import { CustomerModal } from '../components/CustomerModal';

export function Customers() {
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [timeframe, setTimeframe] = useState({
    start: '',
    end: '',
    ticks: [] as string[]
  });

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const [customersResponse, projectsResponse, projectionsResponse] = await Promise.all([
        supabase
          .from('customers')
          .select('*')
          .order('name'),
        supabase
          .from('projects')
          .select(`
            id,
            project_name,
            budget,
            year,
            month,
            duration:durations (
              name
            ),
            customer_id
          `)
          .not('budget', 'is', null),
        supabase
          .from('projections')
          .select('*')
          .eq('status', 'alive')
      ]);

      if (customersResponse.error) throw customersResponse.error;
      if (projectsResponse.error) throw projectsResponse.error;
      if (projectionsResponse.error) throw projectionsResponse.error;

      // Create a set of customer IDs with active projections
      const customersWithProjections = new Set(
        projectionsResponse.data.map(projection => projection.customer_id)
      );

      const monthToNumber: { [key: string]: number } = {
        'January': 0, 'February': 1, 'March': 2, 'April': 3,
        'May': 4, 'June': 5, 'July': 6, 'August': 7,
        'September': 8, 'October': 9, 'November': 10, 'December': 11
      };

      const years = new Set<string>();
      
      projectsResponse.data.forEach((project: any) => {
        if (project.year) {
          years.add(project.year);
        }
      });

      projectionsResponse.data.forEach((projection: any) => {
        if (projection.year) {
          years.add(projection.year);
        }
      });

      const allYears = Array.from(years).map(year => parseInt(year));
      const minYear = Math.min(2018, ...allYears);
      const maxYear = Math.max(...allYears);

      const ticks = Array.from(
        { length: maxYear - minYear + 1 },
        (_, i) => (minYear + i).toString()
      );

      setTimeframe({
        start: `${minYear}-01`,
        end: `${maxYear}-12`,
        ticks
      });

      const currentYear = new Date().getFullYear().toString();
      const previousYear = (parseInt(currentYear) - 1).toString();

      const customersWithData = customersResponse.data.map(customer => {
        const customerProjects = projectsResponse.data.filter(
          (project: any) => project.customer_id === customer.id
        );

        const projectNames = customerProjects.map((project: any) => project.project_name);

        const customerProjections = projectionsResponse.data.filter(
          (projection: any) => projection.customer_id === customer.id
        );

        const yearlyData = new Map<string, {
          confirmed: number;
          projected: number;
          weighted: number;
          minimum: number;
        }>();

        let totalBudget = 0;
        let recentActivity = 0;
        let futureValue = 0;

        customerProjects.forEach((project: any) => {
          if (!project.budget || !project.year || !project.month || !project.duration) return;

          const budget = parseFloat(project.budget.replace(/[^0-9.-]+/g, ''));
          if (isNaN(budget)) return;

          const startYear = parseInt(project.year);
          const startMonth = monthToNumber[project.month];
          const durationMonths = parseInt(project.duration.name.match(/\d+/)[0]);
          const monthlyBudget = budget / durationMonths;

          let remainingMonths = durationMonths;
          let currentYearIter = startYear;
          let currentMonth = startMonth;

          while (remainingMonths > 0) {
            const monthsInYear = Math.min(remainingMonths, 12 - currentMonth);
            const yearContribution = monthlyBudget * monthsInYear;
            const yearStr = currentYearIter.toString();

            const yearData = yearlyData.get(yearStr) || {
              confirmed: 0,
              projected: 0,
              weighted: 0,
              minimum: 0
            };

            yearData.confirmed += yearContribution;

            if (yearStr >= currentYear) {
              yearData.projected += yearContribution;
              yearData.weighted += yearContribution;
              yearData.minimum += yearContribution;
              futureValue += yearContribution;
            }

            if (yearStr === currentYear || yearStr === previousYear) {
              recentActivity += yearContribution;
            }

            yearlyData.set(yearStr, yearData);
            totalBudget += yearContribution;

            remainingMonths -= monthsInYear;
            currentYearIter++;
            currentMonth = 0;
          }
        });

        customerProjections.forEach((projection: any) => {
          const value = parseFloat(projection.value.replace(/[^0-9.-]+/g, ''));
          if (isNaN(value)) return;

          const yearStr = projection.year;
          if (yearStr >= currentYear) {
            const yearData = yearlyData.get(yearStr) || {
              confirmed: 0,
              projected: 0,
              weighted: 0,
              minimum: 0
            };

            yearData.projected += value;
            yearData.weighted += (value * projection.confidence / 100);
            yearlyData.set(yearStr, yearData);
            futureValue += (value * projection.confidence / 100);
          }
        });

        const budgetData = Array.from(yearlyData.entries())
          .map(([year, data]) => ({
            year,
            ...data
          }))
          .sort((a, b) => a.year.localeCompare(b.year));

        return {
          ...customer,
          budgetData,
          totalBudget,
          activeProjects: customerProjects.length,
          projectNames,
          recentActivity,
          futureValue,
          score: recentActivity * 0.4 + futureValue * 0.6,
          hasBudget: totalBudget > 0,
          hasProjections: customersWithProjections.has(customer.id)
        };
      });

      // Sort customers with the following priority:
      // 1. Customers with projections
      // 2. Customers with budget but no projections
      // 3. Remaining customers, sorted by score
      const sortedCustomers = customersWithData.sort((a, b) => {
        // First, prioritize customers with projections
        if (a.hasProjections && !b.hasProjections) return -1;
        if (!a.hasProjections && b.hasProjections) return 1;

        // Then, within each group (with/without projections), prioritize those with budget
        if (a.hasProjections === b.hasProjections) {
          if (a.hasBudget && !b.hasBudget) return -1;
          if (!a.hasBudget && b.hasBudget) return 1;
        }

        // Finally, sort by score within each group
        return b.score - a.score;
      });

      setCustomers(sortedCustomers);
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerUpdate = (updatedCustomer: Customer) => {
    loadCustomers();
    setSelectedCustomer(null);
    setShowNewCustomer(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading customers...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <Building2 className="w-8 h-8 text-indigo-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          </div>
          <button
            onClick={() => setShowNewCustomer(true)}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Customer
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {customers.map(customer => (
            <div
              key={customer.id}
              onClick={() => setSelectedCustomer(customer)}
              className="cursor-pointer"
            >
              <CustomerCard customer={customer} timeframe={timeframe} />
            </div>
          ))}
        </div>

        {(selectedCustomer || showNewCustomer) && (
          <CustomerModal
            customer={selectedCustomer || {
              id: '',
              name: '',
              contacts: []
            }}
            onClose={() => {
              setSelectedCustomer(null);
              setShowNewCustomer(false);
            }}
            onSave={handleCustomerUpdate}
            isNew={showNewCustomer}
          />
        )}
      </div>
    </div>
  );
}